import classNames from 'classnames'
import Link from 'next/link'
import { useState } from 'react'
import Button from 'react-bootstrap/Button'
import BSCard from 'react-bootstrap/Card'

import Card from '@/components/Card/Card'
import OutboundLink from '@/components/OutboundLink/OutboundLink'
import { CommandersPageProps } from '@/types/page'
import useDirectories from '@/utils/useDirectories'

import styles from './BottomPanel.module.scss'
import DecklistPanel from './DecklistPanel/DecklistPanel'
import Description from './Description/Description'
import ManaCurvePanel from './ManaCurvePanel/ManaCurvePanel'
import OutboundBar from './OutboundBar/OutboundBar'
import RankOverTimePanel from './RankOverTimePanel/RankOverTimePanel'
import TypeDistributionPanel from './TypeDistributionPanel/TypeDistributionPanel'

type Props = {
  data: CommandersPageProps['data']
}

const BottomPanel = ({ data }: Props) => {
  const card = data?.container?.json_dict?.card
  const comboCounts = data.panels?.combocounts
  const deck = data.deck
  const description = data.description
  const manaCurve = data.panels?.mana_curve
  const pieChart = data.panels?.piechart
  const rankOverTime = data.panels?.rank_over_time
  const similar = data.similar

  const { dir } = useDirectories()
  const [selected, setSelected] = useState<string>()

  const hasCharts = pieChart || manaCurve || rankOverTime
  const hasCombos = comboCounts?.length > 0
  const hasDeck = deck && ['average-decks', 'deckpreview', 'precon'].includes(dir)
  const hasDescription = description?.length > 0
  const hasSimilar = similar?.length > 0

  if (!hasCharts && !hasCombos && !hasDeck && !hasDescription && !hasSimilar) return null

  if (hasDescription) {
    return <Description>{description}</Description>
  }

  return (
    <>
      {(hasCharts || hasCombos || hasDescription || hasSimilar || (card && dir !== 'precon')) && (
        <BSCard className={classNames(styles.container, 'shadow-sm')}>
          <div className={styles.containerInner}>
            <div className='d-flex justify-content-start'>
              <div className='align-items-center d-flex gap-2 justify-content-between mx-2'>
                {hasCharts && (
                  <Button
                    onClick={() => setSelected(selected === 'charts' ? undefined : 'charts')}
                    size='sm'
                    variant={selected === 'charts' ? 'primary' : 'secondary'}
                  >
                    Charts
                  </Button>
                )}
                {hasCombos && (
                  <Button
                    onClick={() => setSelected(selected === 'combos' ? undefined : 'combos')}
                    size='sm'
                    variant={selected === 'combos' ? 'primary' : 'secondary'}
                  >
                    Combos
                  </Button>
                )}
                {hasDescription && (
                  <Button disabled size='sm' variant='secondary'>
                    Description
                  </Button>
                )}
                {hasSimilar && (
                  <Button
                    className='d-flex flex-row'
                    onClick={() => setSelected(selected === 'similar' ? undefined : 'similar')}
                    size='sm'
                    title={`Similar ${dir === 'cards' ? 'Cards' : 'Commanders'}`}
                    variant={selected === 'similar' ? 'primary' : 'secondary'}
                  >
                    <span>Similar</span>
                    <span className='d-none d-sm-block'>&nbsp;{dir === 'cards' ? 'Cards' : 'Commanders'}</span>
                  </Button>
                )}
              </div>
            </div>
            {card && dir !== 'precon' && <OutboundBar card={card} />}
          </div>
          {hasCharts && selected === 'charts' && (
            <div>
              <div className={styles.charts}>
                {pieChart && <TypeDistributionPanel chartData={pieChart} />}
                {manaCurve && <ManaCurvePanel curve={manaCurve} />}
              </div>
              {rankOverTime && <RankOverTimePanel rankOverTime={rankOverTime} />}
            </div>
          )}
          {hasCombos && selected === 'combos' && (
            <div className='mx-2 mb-2'>
              <span>
                <b>Top Combos</b>
              </span>
              <div className='ms-4'>
                {comboCounts.map(({ href, value }) => (
                  <div key={href}>
                    <Link href={href}>{value}</Link>
                  </div>
                ))}
              </div>
              <div className='d-flex flex-column mt-2'>
                <div>
                  <b>More Combos</b>
                </div>
                <div className='d-flex gap-2 ms-4 mt-1'>
                  <Link href={`/combos/${card.sanitized}`}>
                    <Button size='sm' variant='secondary'>
                      EDHREC
                    </Button>
                  </Link>
                  <OutboundLink
                    title='Combo Spellbook'
                    url={`https://commanderspellbook.com/search/?q=${encodeURIComponent(`card:"${card?.name}"`)}`}
                    utm_medium='combos_panel'
                  >
                    <Button size='sm' variant='secondary'>
                      Commander Spellbook
                    </Button>
                  </OutboundLink>
                </div>
              </div>
            </div>
          )}
          {hasSimilar && selected === 'similar' && (
            <div className={styles.similar}>
              {similar?.map((card) => (
                <div className={styles.card} key={card.name}>
                  <Card card={card} label={card.name} />
                </div>
              ))}
            </div>
          )}
        </BSCard>
      )}
      {hasDeck && (
        <BSCard className={classNames('shadow-sm rounded-0 rounded-bottom-3')}>
          <DecklistPanel data={data} />
        </BSCard>
      )}
    </>
  )
}

export default BottomPanel
